// Mesh Canvas component - renders the interactive mesh visualization
import { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMeshCanvas } from '../hooks/useMeshCanvas';
import type { MeshControls } from '../types';

// Mini tooltip component for stats overlay
function StatsTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<'center' | 'left' | 'right'>('center');
  
  useEffect(() => {
    if (show && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 192; // w-48 = 12rem = 192px
      const viewportWidth = window.innerWidth;
      
      // Check if tooltip would go off the right edge
      if (rect.left + tooltipWidth / 2 > viewportWidth - 10) {
        setPosition('right');
      }
      // Check if tooltip would go off the left edge
      else if (rect.left - tooltipWidth / 2 < 10) {
        setPosition('left');
      } else {
        setPosition('center');
      }
    }
  }, [show]);
  
  const positionClasses = {
    center: 'left-1/2 -translate-x-1/2',
    left: 'left-0',
    right: 'right-0',
  };
  
  const arrowClasses = {
    center: 'left-1/2 -translate-x-1/2',
    left: 'left-4',
    right: 'right-4',
  };
  
  return (
    <div className="relative inline-block ml-1">
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => {
          e.stopPropagation();
          setShow(!show);
        }}
        className="text-zinc-500 hover:text-cyan-400 transition-colors"
        aria-label="More information"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      
      {show && (
        <div
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          className={`absolute ${positionClasses[position]} top-full mt-1 z-[200] w-48 p-2 bg-zinc-800 border border-cyan-500/50 rounded-lg shadow-2xl text-[10px] text-zinc-200 leading-relaxed pointer-events-auto`}
        >
          {text}
          <div className={`absolute -top-1 ${arrowClasses[position]} w-2 h-2 bg-zinc-800 border-l border-t border-cyan-500/50 transform rotate-45`}></div>
        </div>
      )}
    </div>
  );
}

interface MeshCanvasProps {
  controls: MeshControls;
  onControlsChange?: (updates: Partial<MeshControls>) => void;
  regenerateTrigger?: number;
  children?: (mesh: { nodes: any[]; edges: any[] }, activePackets: number) => React.ReactNode;
}

export function MeshCanvas({ controls, onControlsChange, regenerateTrigger, children }: MeshCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const location = useLocation();
  const { mesh, activePackets, hoveredNode, hoveredNodePos, hoveredPacket, hoveredPacketPos } = useMeshCanvas(
    canvasRef,
    controls.nodeCount,
    controls.traffic,
    controls.lineOpacity,
    controls.neighborK,
    controls.pulseColor,
    controls.topologyMode,
    controls.maxRange,
    regenerateTrigger,
    controls.playgroundMode,
    controls.playgroundInteractionMode,
    controls.zoomLevel,
    controls.paused
  );
  
  // Track mode changes - delayed fade in for playground overlays
  const [overlayOpacity, setOverlayOpacity] = useState(controls.playgroundMode ? 1 : 0);
  const prevModeRef = useRef(controls.playgroundMode);
  
  // Track if we're on mobile for canvas extension
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Track if user is at bottom of page to hide vignette
  const [isAtBottom, setIsAtBottom] = useState(false);
  
  // Check if we're on 404 page (any unmatched route)
  const is404Page = !['/', '/community-resources', '/mesh-tools'].includes(location.pathname);
  
  // Playground overlays visibility toggle
  const [showPlaygroundOverlays, setShowPlaygroundOverlays] = useState(true);
  
  // Welcome toast state
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const [toastDismissing, setToastDismissing] = useState(false);
  const hasShownToastThisSessionRef = useRef(false);
  
  // Show welcome toast when entering playground mode for first time in session
  useEffect(() => {
    // Check if user has dismissed toast this session (sessionStorage clears on tab close)
    const dismissedThisSession = sessionStorage.getItem('mesh-welcome-dismissed') === 'true';
    
    console.log('[Toast Debug] Playground mode:', controls.playgroundMode, 'Dismissed this session:', dismissedThisSession, 'Shown this session:', hasShownToastThisSessionRef.current);
    
    // Show toast if: entering playground, not dismissed this session, and not shown this session yet
    if (controls.playgroundMode && !dismissedThisSession && !hasShownToastThisSessionRef.current) {
      // Delay to let playground mode transition and overlays fade in complete
      const timer = setTimeout(() => {
        console.log('[Toast Debug] Showing welcome toast');
        setShowWelcomeToast(true);
        hasShownToastThisSessionRef.current = true;
      }, 1200); // Match overlay fade-in delay (800ms) + buffer
      return () => clearTimeout(timer);
    }
    
    // Hide toast when leaving playground mode
    if (!controls.playgroundMode && showWelcomeToast) {
      setShowWelcomeToast(false);
      setToastDismissing(false);
    }
  }, [controls.playgroundMode, showWelcomeToast]);
  
  // Dismiss welcome toast with animation (stores in sessionStorage only)
  const dismissWelcomeToast = () => {
    setToastDismissing(true);
    setTimeout(() => {
      setShowWelcomeToast(false);
      // Use sessionStorage so toast reappears on new page load/tab
      sessionStorage.setItem('mesh-welcome-dismissed', 'true');
    }, 300); // Match animation duration
  };
  
  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Detect when user scrolls near footer
  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;
      
      // Consider "at bottom" when within 200px of footer
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      setIsAtBottom(distanceFromBottom < 200);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    if (prevModeRef.current !== controls.playgroundMode) {
      prevModeRef.current = controls.playgroundMode;
      
      if (controls.playgroundMode) {
        // Switching to playground: wait for header to slide up, then fade in overlays
        const timer = setTimeout(() => {
          setOverlayOpacity(1);
        }, 800); // Wait for header slide-up (800ms) before fading in
        return () => clearTimeout(timer);
      } else {
        // Switching to normal: immediately start fading out overlays
        setOverlayOpacity(0);
      }
    }
  }, [controls.playgroundMode]);

  return (
    <>
      {children && children(mesh, activePackets)}
      
      {/* Welcome Toast - Under Header (only in playground mode) */}
      {showWelcomeToast && controls.playgroundMode && (
        <div 
          className={`fixed top-16 md:top-20 left-1/2 z-[100] pointer-events-auto w-[90%] max-w-2xl ${toastDismissing ? 'animate-out' : 'animate-in'}`}
        >
          <div className="bg-cyan-500/10 backdrop-blur-md border border-cyan-500/30 rounded-xl p-4 md:p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-semibold text-cyan-400 mb-1">
                  Welcome to the Interactive Mesh Network Simulator!
                </h3>
                <p className="text-xs md:text-sm text-zinc-300 leading-relaxed">
                  Watch packets route through the mesh in real-time. Click <strong className="text-cyan-400">Playground</strong> in 
                  the header to interact with the network, add nodes, and explore Meshtastic-style flood routing.
                </p>
              </div>
              <button
                onClick={dismissWelcomeToast}
                className="flex-shrink-0 text-zinc-400 hover:text-white transition-colors p-1"
                aria-label="Dismiss welcome message"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="fixed pointer-events-none inset-0" 
        style={{ 
          zIndex: 0,
        }}
      >
        {/* Mobile: Extend canvas container beyond viewport */}
        <div 
          className="absolute inset-0"
          style={{
            // On mobile only, extend 30% beyond viewport edges for more breathing room
            left: isMobile ? '-30%' : 0,
            right: isMobile ? '-30%' : 0,
            width: isMobile ? '160%' : '100%',
            height: '100%',
          }}
        >
        <canvas 
        ref={canvasRef} 
            className={`h-full w-full ${controls.playgroundMode ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{ width: '100%', height: '100%', display: 'block' }} 
      />
        </div>
      </div>
      
      {/* Subtle vignette for depth - positioned relative to viewport (toned down) */}
      <div 
        className="pointer-events-none fixed inset-0 bg-gradient-to-b from-zinc-950/5 via-transparent to-zinc-950/60 transition-opacity duration-300" 
        style={{ 
          zIndex: 1,
          opacity: (isAtBottom || is404Page) ? 0 : 1
        }} 
      />
      
      {/* Toggle Overlays Button (only in playground mode) */}
      {controls.playgroundMode && (
        <button
          onClick={() => setShowPlaygroundOverlays(!showPlaygroundOverlays)}
          className="fixed top-20 md:top-24 right-4 md:right-6 bg-zinc-900/90 backdrop-blur-sm border border-zinc-700/50 rounded-lg p-2 md:p-2.5 shadow-xl hover:bg-zinc-800/90 hover:border-cyan-500/50 transition-all z-[15]"
          style={{ opacity: overlayOpacity }}
          aria-label={showPlaygroundOverlays ? 'Hide overlays' : 'Show overlays'}
          title={showPlaygroundOverlays ? 'Hide stats & controls' : 'Show stats & controls'}
        >
          {showPlaygroundOverlays ? (
            <svg className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          ) : (
            <svg className="w-4 h-4 md:w-5 md:h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      )}
      
      {/* Distance Scale Overlay (only in playground mode + radius) */}
      {controls.playgroundMode && controls.topologyMode === 'radius' && showPlaygroundOverlays && (
        <div 
          className="fixed bottom-4 md:bottom-6 right-4 md:right-6 bg-zinc-900/90 backdrop-blur-sm border border-zinc-700/50 rounded-lg px-3 md:px-4 py-2 md:py-3 pointer-events-none transition-all duration-300"
          style={{ opacity: overlayOpacity, zIndex: 10 }}
        >
          <div className="text-[10px] md:text-xs text-zinc-400 mb-1.5 md:mb-2 font-medium">Connection Range</div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div 
                className="w-16 h-1 bg-gradient-to-r from-cyan-500/30 to-cyan-500 rounded-full"
                style={{ 
                  width: `${controls.maxRange * 200 * controls.zoomLevel}px`,
                  maxWidth: '120px'
                }}
              />
              <div className="absolute -right-1 -top-1 w-3 h-3 bg-cyan-400 rounded-full border-2 border-zinc-900" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs md:text-sm text-cyan-400 font-mono">
                {controls.distanceUnit === 'mi' 
                  ? `${(controls.maxRange * 50).toFixed(1)} mi`
                  : `${(controls.maxRange * 80).toFixed(1)} km`
                }
              </span>
              <span className="text-[9px] md:text-[10px] text-zinc-500">
                ({Math.round(controls.maxRange * 100)}% canvas)
              </span>
            </div>
          </div>
          <div className="text-[9px] md:text-[10px] text-zinc-500 mt-1 md:mt-1.5">
            ≈ LoRa radio range • Zoom: {Math.round(controls.zoomLevel * 100)}%
          </div>
        </div>
      )}
      
      {/* Zoom Controls (only in playground mode) */}
      {controls.playgroundMode && showPlaygroundOverlays && (
        <div 
          className="fixed bottom-4 md:bottom-6 left-4 md:left-6 bg-zinc-900/90 backdrop-blur-sm border border-zinc-700/50 rounded-lg px-2 md:px-3 py-1.5 md:py-2 shadow-xl pointer-events-auto transition-all duration-300"
          style={{ opacity: overlayOpacity, zIndex: 10 }}
        >
        <div className="text-[10px] md:text-xs text-zinc-400 mb-1.5 md:mb-2 font-medium flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </svg>
          Zoom
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onControlsChange?.({ zoomLevel: Math.max(0.5, controls.zoomLevel - 0.1) })}
            className="w-6 h-6 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded transition-colors"
            aria-label="Zoom out"
            disabled={controls.zoomLevel <= 0.5}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={controls.zoomLevel}
              onChange={(e) => onControlsChange?.({ zoomLevel: parseFloat(e.target.value) })}
              className="w-24 h-1"
              aria-label="Zoom level"
            />
            <span className="text-xs text-cyan-400 font-mono min-w-[2.5rem] text-right">{Math.round(controls.zoomLevel * 100)}%</span>
          </div>
          <button
            onClick={() => onControlsChange?.({ zoomLevel: Math.min(2.0, controls.zoomLevel + 0.1) })}
            className="w-6 h-6 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded transition-colors"
            aria-label="Zoom in"
            disabled={controls.zoomLevel >= 2.0}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>
      )}
      
      {/* Mesh Stats Overlay (only in playground mode) */}
      {controls.playgroundMode && mesh && showPlaygroundOverlays && (
        <div 
          className="fixed top-16 md:top-20 left-2 md:left-4 lg:left-6 bg-zinc-900/90 backdrop-blur-sm border border-zinc-700/50 rounded-lg px-3 md:px-4 py-2 md:py-3 shadow-xl pointer-events-auto transition-all duration-300 overflow-visible text-xs md:text-sm"
          style={{ opacity: overlayOpacity, maxWidth: 'calc(100vw - 1rem)', zIndex: 10 }}
        >
          <div className="text-[10px] md:text-xs text-zinc-400 mb-2 md:mb-3 font-medium flex items-center justify-between gap-2 md:gap-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Mesh Statistics
            </div>
            {/* Mode Toggle */}
            <button
              onClick={() => {
                const newMode = controls.playgroundInteractionMode === 'pan' ? 'addNodes' : 'pan';
                onControlsChange?.({ playgroundInteractionMode: newMode });
              }}
              className={`px-2 md:px-2.5 py-0.5 md:py-1 rounded text-[9px] md:text-[10px] font-medium transition-all ${
                controls.playgroundInteractionMode === 'pan'
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'bg-emerald-500/20 text-emerald-400'
              }`}
              title={controls.playgroundInteractionMode === 'pan' ? 'Switch to Add Nodes mode' : 'Switch to Pan mode'}
            >
              {controls.playgroundInteractionMode === 'pan' ? (
                <span className="flex items-center gap-1">
                  <svg className="w-2.5 h-2.5 md:w-3 md:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                  </svg>
                  <span className="hidden sm:inline">PAN MODE</span>
                  <span className="sm:hidden">PAN</span>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <svg className="w-2.5 h-2.5 md:w-3 md:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">ADD MODE</span>
                  <span className="sm:hidden">ADD</span>
                </span>
              )}
            </button>
          </div>
          <div className="space-y-1 md:space-y-1.5 text-[10px] md:text-xs">
            <div className="flex justify-between gap-3 md:gap-4">
              <span className="text-zinc-500 flex items-center">
                Nodes:
                <StatsTooltip text="Total simulated devices in this visualization. Real mesh networks can have any number of nodes." />
              </span>
              <span className="text-cyan-400 font-mono">{mesh.nodes.length}</span>
            </div>
            <div className="flex justify-between gap-3 md:gap-4">
              <span className="text-zinc-500 flex items-center">
                Connections:
                <StatsTooltip text="Simulated radio links between nodes (bidirectional). Real mesh connections vary by range and topology." />
              </span>
              <span className="text-cyan-400 font-mono">{mesh.edges.length}</span>
            </div>
            <div className="flex justify-between gap-3 md:gap-4 hidden sm:flex">
              <span className="text-zinc-500 flex items-center">
                Avg/Node:
                <StatsTooltip text="Average simulated connections per node in this visualization. Real mesh density varies by deployment." />
              </span>
              <span className="text-cyan-400 font-mono">
                {mesh.nodes.length > 0 ? ((mesh.edges.length * 2) / mesh.nodes.length).toFixed(1) : '0'}
              </span>
            </div>
            <div className="flex justify-between gap-3 md:gap-4">
              <span className="text-zinc-500 flex items-center">
                Packets:
                <StatsTooltip text="Simulated messages traveling through this visualization. Demonstrates packet flow, not actual Meshtastic traffic." />
              </span>
              <span className="text-yellow-400 font-mono">{activePackets}</span>
            </div>
            <div className="flex justify-between gap-3 md:gap-4 hidden sm:flex">
              <span className="text-zinc-500 flex items-center">
                Density:
                <StatsTooltip text="Simulated network interconnection ratio (actual vs. possible connections in this visualization)." />
              </span>
              <span className="text-cyan-400 font-mono">
                {mesh.nodes.length > 1 
                  ? `${Math.round((mesh.edges.length / ((mesh.nodes.length * (mesh.nodes.length - 1)) / 2)) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
            <div className="flex justify-between gap-3 md:gap-4">
              <span className="text-zinc-500 flex items-center">
                Mode:
                <StatsTooltip text={controls.topologyMode === 'radius' ? 'Realistic range-based connections' : 'Simplified k-nearest neighbors'} />
              </span>
              <span className={`font-mono ${controls.topologyMode === 'radius' ? 'text-emerald-400' : 'text-blue-400'}`}>
                {controls.topologyMode.toUpperCase()}
              </span>
            </div>
          </div>
          {controls.playgroundMode && (
            <div className="mt-2 md:mt-3 pt-1.5 md:pt-2 border-t border-zinc-700/50 space-y-1.5 md:space-y-2">
              <div className="text-[9px] md:text-[10px] text-zinc-500 space-y-1">
                {controls.playgroundInteractionMode === 'pan' ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-2.5 h-2.5 md:w-3 md:h-3 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                      </svg>
                      <span><strong className="text-zinc-400">Pan Mode:</strong> <span className="hidden sm:inline">Drag canvas to pan • Drag nodes to move</span><span className="sm:hidden">Drag to move</span></span>
                    </div>
                    <div className="pl-0 sm:pl-5 text-zinc-500 italic">
                      Click mode toggle above to switch to Add Mode
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-2.5 h-2.5 md:w-3 md:h-3 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span><strong className="text-emerald-400">Add Mode:</strong> <span className="hidden sm:inline">Click anywhere to add nodes</span><span className="sm:hidden">Click to add</span></span>
                    </div>
                    <div className="pl-0 sm:pl-5 text-zinc-500 italic">
                      Click mode toggle above to switch to Pan Mode
                    </div>
                  </>
                )}
              </div>
              <div className="text-[9px] md:text-[10px] text-zinc-600 italic">
                <span className="hidden sm:inline whitespace-pre-line break-words">
                Hover over nodes to see connection info.
                {'\n'}
                Pause simulation to see packet info on hover.
                </span>
                <span className="sm:hidden">Hover nodes for info • Pause to inspect packets</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Node Hover Info Overlay */}
      {hoveredNode !== null && hoveredNodePos && mesh && (
        <div
          className="absolute pointer-events-none bg-zinc-900/95 backdrop-blur-sm border border-cyan-500/50 rounded-lg px-3 py-2 shadow-xl z-50"
          style={{
            left: `${hoveredNodePos.x + 15}px`,
            top: `${hoveredNodePos.y - 10}px`,
          }}
        >
          <div className="text-xs space-y-1">
            <div className="text-cyan-400 font-semibold mb-1.5 flex items-center gap-1.5">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="8" />
              </svg>
              Node #{hoveredNode + 1}
            </div>
            <div className="text-zinc-300 space-y-0.5">
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Connections:</span>
                <span className="font-mono text-cyan-400">
                  {mesh.edges.filter(e => e.a === hoveredNode || e.b === hoveredNode).length}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Position:</span>
                <span className="font-mono text-xs text-zinc-400">
                  ({mesh.nodes[hoveredNode].x.toFixed(2)}, {mesh.nodes[hoveredNode].y.toFixed(2)})
                </span>
              </div>
            </div>
            <div className="text-[10px] text-zinc-600 italic mt-1.5 pt-1.5 border-t border-zinc-700/50">
              Drag to reposition node
            </div>
          </div>
        </div>
      )}
      
      {/* Packet Hover Info Overlay (visible in all modes when paused) */}
      {hoveredPacket && hoveredPacketPos && mesh && (
        <div
          className="absolute pointer-events-none bg-zinc-900/95 backdrop-blur-sm border rounded-lg px-3 py-2 shadow-xl z-50"
          style={{
            left: `${hoveredPacketPos.x + 15}px`,
            top: `${hoveredPacketPos.y - 10}px`,
            borderColor: hoveredPacket.color,
          }}
        >
          <div className="text-xs space-y-1">
            <div className="font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: hoveredPacket.color }}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Message Packet
            </div>
            <div className="text-zinc-300 space-y-0.5">
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Type:</span>
                <span className="font-mono font-medium" style={{ color: hoveredPacket.color }}>
                  {hoveredPacket.messageType}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Route:</span>
                <span className="font-mono text-xs text-zinc-400">
                  #{hoveredPacket.path[0] + 1} → #{hoveredPacket.path[hoveredPacket.path.length - 1] + 1}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Current Hop:</span>
                <span className="font-mono text-cyan-400">
                  {hoveredPacket.currentHop + 1} / {hoveredPacket.path.length - 1}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Hops Remaining:</span>
                <span className="font-mono text-emerald-400">
                  {hoveredPacket.hopsRemaining} / {hoveredPacket.hopLimit}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Path Length:</span>
                <span className="font-mono text-zinc-400">
                  {hoveredPacket.path.length} nodes
                </span>
              </div>
            </div>
            <div className="text-[10px] text-zinc-600 italic mt-1.5 pt-1.5 border-t border-zinc-700/50">
              Simulation paused • Resume to continue
            </div>
          </div>
        </div>
      )}
    </>
  );
}


